import { FC, ReactElement, useState, useEffect, useMemo } from "react";
import { Box, Grid, CircularProgress } from "@mui/material";
import { useGetPotentialFTZSavingsQuery } from "@/services/dashboard/dashboard-api";
import { Filters, MetricsCard, SavingsCard, FTZMap } from "./components";
import { SelectClientBanner } from "@/components";
import { useAppSelector } from "@/store";
import { Range } from "react-date-range";
import { format } from "date-fns";
import { formatNumberWithSuffix } from "@/utils";
import { roles } from "@/roles";

const FTZ: FC = (): ReactElement => {
  const { user } = useAppSelector((state) => state.auth);
  const { selectedClient } = useAppSelector((state) => state.client);
  const { selectedTenant } = useAppSelector((state) => state.tenantManagement);
  const [currentSort, setCurrentSort] = useState<FTZSortType>('lines');
  const [dateFilter] = useState<Pick<Range, "startDate" | "endDate">>({
    startDate: new Date("1 Jan 2023"),
    endDate: new Date(),
  });

  const canSelectClients = useMemo(() => {
    const allowedRoles = [roles.SPOWNER, roles.SPPARTNER, roles.TDADMIN] as string[];
    return allowedRoles.includes(user?.userRole ?? "");
  }, [user]);

  const isTDAdminTCD = useMemo(() => {
    return user?.userRole === roles.TDADMIN && selectedTenant?.tenantType === "TRADEDIVE_CLIENT";
  }, [user, selectedTenant]);

  const [filters, setFilters] = useState<IFTZFilters>({
    selectedPorts: [], // Will be populated with all ports initially
    lineRange: { min: 0, max: 0 },
    dutySpendRange: { min: 0, max: 0 },
    mpfSpendRange: { min: 0, max: 0 },
  });

  const [metrics, setMetrics] = useState<IFTZMetrics>({
    entryLines: 0,
    ports: 0,
    weeks: 0,
    oldEntrySummaries: 0,
    newEntrySummaries: 0,
  });

  const [savings, setSavings] = useState<IFTZSavings>({
    oldEntryCost: 0,
    newEntryCost: 0,
    entryCostSavings: 0,
    oldMPF: 0,
    newMPF: 0,
    mpfSavings: 0,
  });

  const [oldCostPerEntry, setOldCostPerEntry] = useState(100);
  const [newCostPerEntry, setNewCostPerEntry] = useState(1500);

  const { data: ftzData, isLoading, isFetching } = useGetPotentialFTZSavingsQuery({
    dashboardPotential: {
      dateFrom: format(dateFilter.startDate ?? new Date(), "yyyy-MM-dd"),
      dateTo: format(dateFilter.endDate ?? new Date(), "yyyy-MM-dd"),
      clientId: selectedClient?._id,
      ...(isTDAdminTCD && {
        tenantId: selectedTenant?._id,
      }),
    },
  });

  const filteredEntries = useMemo(() => {
    if (!ftzData?.data) return [];
    
    return ftzData.data.filter((entry: IFTZEntry) => {
      const portMatch = filters.selectedPorts.length === 0 || filters.selectedPorts.includes(entry.portOfEntry);
      const lineMatch = entry.lineCount >= filters.lineRange.min && 
                       (filters.lineRange.max === 0 || entry.lineCount <= filters.lineRange.max);
      const dutyMatch = entry.totalDuties >= filters.dutySpendRange.min && 
                       (filters.dutySpendRange.max === 0 || entry.totalDuties <= filters.dutySpendRange.max);
      const mpfMatch = entry.mpf >= filters.mpfSpendRange.min && 
                      (filters.mpfSpendRange.max === 0 || entry.mpf <= filters.mpfSpendRange.max);

      return portMatch && lineMatch && dutyMatch && mpfMatch;
    });
  }, [ftzData?.data, filters]);

  const calculateMetrics = (entries: IFTZEntry[]) => {
    if (!entries || entries.length === 0) {
      setMetrics({
        entryLines: 0,
        ports: 0,
        weeks: 0,
        oldEntrySummaries: 0,
        newEntrySummaries: 0,
      });
      return;
    }

    const totalLines = entries.reduce((sum, entry) => sum + entry.lineCount, 0);
    const uniquePorts = new Set(entries.map(entry => entry.portOfEntry));
    const oldEntrySummaries = entries.length;
    const newEntrySummaries = Math.ceil(totalLines / 998);
    
    // Simply use newEntrySummaries as weeks
    const weeks = newEntrySummaries;

    setMetrics({
      entryLines: totalLines,
      ports: uniquePorts.size,
      weeks,
      oldEntrySummaries,
      newEntrySummaries,
    });
  };

  const calculateSavings = (entries: IFTZEntry[]) => {
    if (!entries || entries.length === 0) {
      setSavings({
        oldEntryCost: 0,
        newEntryCost: 0,
        entryCostSavings: 0,
        oldMPF: 0,
        newMPF: 0,
        mpfSavings: 0,
      });
      return;
    }

    const MIN_MPF = 33.37;
    const MAX_MPF = 575.35;
    const MAX_LINES_PER_SUMMARY = 998;
    
    const oldMPF = entries.reduce((sum, entry) => sum + entry.mpf, 0);
    let newMPF = 0;
    let currentBundleLines = 0;
    let currentBundleMPF = 0;

    // Sort entries by date to bundle them chronologically
    const sortedEntries = [...entries].sort((a, b) => 
      new Date(a.entryDate).getTime() - new Date(b.entryDate).getTime()
    );

    sortedEntries.forEach((entry) => {
      // Add to current bundle
      currentBundleLines += entry.lineCount;
      currentBundleMPF += entry.totalLineMPF; // Using totalLineMPF for accurate MPF calculation

      // If bundle is full or it's the last entry
      if (currentBundleLines >= MAX_LINES_PER_SUMMARY) {
        // Calculate MPF for current bundle
        newMPF += Math.max(MIN_MPF, Math.min(currentBundleMPF, MAX_MPF));
        // Reset bundle
        currentBundleLines = 0;
        currentBundleMPF = 0;
      }
    });

    // Handle remaining bundle if exists
    if (currentBundleLines > 0) {
      newMPF += Math.max(MIN_MPF, Math.min(currentBundleMPF, MAX_MPF));
    }

    const oldEntryCost = entries.length * oldCostPerEntry;
    const newEntryCost = Math.ceil(entries.reduce((sum, entry) => sum + entry.lineCount, 0) / MAX_LINES_PER_SUMMARY) 
      * newCostPerEntry;

    setSavings({
      oldEntryCost,
      newEntryCost,
      entryCostSavings: oldEntryCost - newEntryCost,
      oldMPF: parseFloat(oldMPF.toFixed(2)),
      newMPF: parseFloat(newMPF.toFixed(2)),
      mpfSavings: parseFloat((oldMPF - newMPF).toFixed(2))
    });
  };

  const handleCostChange = (type: 'old' | 'new', value: number) => {
    if (type === 'old') {
      setOldCostPerEntry(value);
    } else {
      setNewCostPerEntry(value);
    }
    
    if (!filteredEntries || filteredEntries.length === 0) {
      setSavings(prev => ({
        ...prev,
        oldEntryCost: 0,
        newEntryCost: 0,
        entryCostSavings: 0,
      }));
      return;
    }
    
    const oldEntryCost = filteredEntries.length * (type === 'old' ? value : oldCostPerEntry);
    const newEntryCost = Math.ceil(
      filteredEntries.reduce((sum: number, entry: IFTZEntry) => sum + entry.lineCount, 0) / 998
    ) * (type === 'new' ? value : newCostPerEntry);

    setSavings(prev => ({
      ...prev,
      oldEntryCost,
      newEntryCost,
      entryCostSavings: oldEntryCost - newEntryCost,
    }));
  };

  // Initialize filters and calculations when data is loaded
  useEffect(() => {
    if (ftzData?.data && ftzData.data.length > 0) {
      const allPorts = [...new Set(ftzData.data.map((entry: IFTZEntry) => entry.portOfEntry))];
      setFilters(prev => ({
        ...prev,
        selectedPorts: allPorts as string[],
        lineRange: { 
          min: 0, 
          max: Math.round(Math.max(...ftzData.data.map((e: IFTZEntry) => e.lineCount)) * 100) / 100
        },
        dutySpendRange: { 
          min: 0, 
          max: Math.round(Math.max(...ftzData.data.map((e: IFTZEntry) => e.totalDuties)) * 100) / 100
        },
        mpfSpendRange: { 
          min: 0, 
          max: Math.round(Math.max(...ftzData.data.map((e: IFTZEntry) => e.mpf)) * 100) / 100
        },
      }));
    } else if (ftzData?.data) {
      // Handle empty data case
      setFilters(prev => ({
        ...prev,
        selectedPorts: [],
        lineRange: { min: 0, max: 0 },
        dutySpendRange: { min: 0, max: 0 },
        mpfSpendRange: { min: 0, max: 0 },
      }));
    }
  }, [ftzData?.data]);

  // Update calculations when filtered entries change
  useEffect(() => {
    calculateMetrics(filteredEntries);
    calculateSavings(filteredEntries);
  }, [filteredEntries]);

  if (isLoading || isFetching) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="calc(100vh - 200px)"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (canSelectClients && !selectedClient && !isTDAdminTCD) {
    return (
      <SelectClientBanner/>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      <Filters
        filters={filters}
        onFiltersChange={setFilters}
        entries={ftzData?.data ?? []}
        currentSort={currentSort}
        onSortChange={setCurrentSort}
        totalEntries={filteredEntries.length}
      />
      
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Grid container spacing={2} sx={{ height: '100%' }}>
            <Grid item xs={12} md={3}>
              <MetricsCard metrics={metrics} />
            </Grid>
            
            <Grid item xs={12} md={9}>
              <Grid container spacing={2} sx={{ height: '100%' }}>
                <Grid item xs={12} md={4}>
                  <SavingsCard 
                    type="entryCost"
                    title="Entry Cost Savings"
                    value={savings.entryCostSavings}
                    oldCostPerEntry={oldCostPerEntry}
                    newCostPerEntry={newCostPerEntry}
                    onCostChange={handleCostChange}
                    oldEntrySummaries={metrics.oldEntrySummaries}
                    newEntrySummaries={metrics.newEntrySummaries}
                  />
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <SavingsCard 
                    type="mpf"
                    title="MPF Savings"
                    value={savings.mpfSavings}
                    calculation={`${formatNumberWithSuffix(savings.oldMPF)} → ${formatNumberWithSuffix(savings.newMPF)}`}
                  />
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <SavingsCard 
                    type="duties"
                    title="Duties Deferred"
                    value={filteredEntries.reduce((sum: number, entry: IFTZEntry) => sum + entry.totalDuties, 0)}
                    goodsValue={filteredEntries.reduce((sum: number, entry: IFTZEntry) => sum + (entry.lineCount * 1000), 0)}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <FTZMap selectedPorts={filters.selectedPorts} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default FTZ; 